import type { StatusBarStyle } from 'expo-status-bar';
import { useColorScheme } from 'nativewind';
import { useMemo } from 'react';

import { BASE_COLORS, DARK_COLORS, LIGHT_COLORS } from '@/constants/colors';

export function useColors() {
  const { colorScheme } = useColorScheme();

  const isDarkScheme = colorScheme === 'dark';

  const currentColorScheme = isDarkScheme ? ('dark' as const) : ('light' as const);

  const statusBarStyle: StatusBarStyle = useMemo(() => {
    return isDarkScheme ? 'light' : 'dark';
  }, [isDarkScheme]);

  const colors = useMemo(
    () => ({ ...BASE_COLORS, ...(isDarkScheme ? DARK_COLORS : LIGHT_COLORS) }),
    [isDarkScheme]
  );

  const theme = useMemo(
    () => ({
      dark: isDarkScheme,
      colors: {
        // The primary color of the app used to tint various elements ( primary brand color )
        primary: colors.primary[500],
        // The color of various backgrounds, such as background color for the screens
        background: colors.background[currentColorScheme],
        //  The background color of card-like elements, such as headers, tab bars etc
        card: colors.background[currentColorScheme],
        // The text color of various elements
        text: colors.typography[currentColorScheme === 'dark' ? 'white' : 900],
        // The color of borders, e.g. header border, tab bar border etc.
        border: colors.outline[200],
        // The color of Tab Navigator badge
        notification: colors.gray,
      },
    }),
    [colors]
  );

  return { colors, theme, currentColorScheme, isDarkScheme, statusBarStyle };
}
